import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { VacanteDTO } from 'src/app/interfaces/vacantes';
import { Response } from 'src/app/interfaces/response';

export interface VacanteFiltros {
  q?: string;
  categoria?: number;
  tiempo?: number;
  modalidad?: number;
  ubicacion?: string;
  pais?: string;
  tipoContrato?: string;
  idModalidadEmpleo?: number;
}

export interface PagedVacantes {
  content: VacanteDTO[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

/** Países que usa activamente nuestro scraper (ver ScraperConfig.apifyLocations en backend) — van primero, en este orden. */
const PAISES_PRIORITARIOS = ['México', 'Argentina', 'Colombia', 'Chile', 'Perú'];

const normalizarPais = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** México y los principales países LATAM de nuestro scraper primero, luego el resto alfabético. */
function ordenarPaises(paises: string[]): string[] {
  const prioridad = PAISES_PRIORITARIOS.map(normalizarPais);
  const esValido = (p: string) => p && normalizarPais(p) !== 'n/a';

  const prioritarios = paises
    .filter(esValido)
    .filter(p => prioridad.includes(normalizarPais(p)))
    .sort((a, b) => prioridad.indexOf(normalizarPais(a)) - prioridad.indexOf(normalizarPais(b)));

  const resto = paises
    .filter(esValido)
    .filter(p => !prioridad.includes(normalizarPais(p)))
    .sort((a, b) => a.localeCompare(b, 'es'));

  return [...prioritarios, ...resto];
}

@Injectable({
  providedIn: 'root'
})
export class VacantesService {

  private baseApiUrl = environment.baseApi;

  constructor(private httpClient: HttpClient) { }

  /** Paginación + filtrado + orden real del lado del servidor. */
  async obtenerVacantesPaginadas(filtros: VacanteFiltros, page: number, size: number): Promise<Response> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    Object.keys(filtros || {}).forEach(key => {
      const value = filtros[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return await this.httpClient.get<Response>(`${this.baseApiUrl}/publico/vacantes/buscar-paginado`, { params }).toPromise();
  }

  /** Países disponibles, con México y el top LATAM de nuestro scraper primero y el resto alfabético. */
  async getPaises(): Promise<string[]> {
    const res = await this.httpClient.get<any>(`${this.baseApiUrl}/publico/vacantes/paises`).toPromise();
    const paises: string[] = res?.data ?? res ?? [];
    return ordenarPaises(paises);
  }

  /** Modalidades activas (Home Office/Presencial/Híbrido) para el filtro público de /vacantes, sin requerir sesión. */
  async getModalidadesEmpleoPublicas(): Promise<any[]> {
    const res = await this.httpClient.get<any>(`${this.baseApiUrl}/publico/vacantes/modalidades-empleo`).toPromise();
    return res?.data ?? res ?? [];
  }

  /** Valores distintos de tipo de contrato entre vacantes activas, para el filtro público de /vacantes. */
  async getTiposContrato(): Promise<string[]> {
    const res = await this.httpClient.get<any>(`${this.baseApiUrl}/publico/vacantes/tipos-contrato`).toPromise();
    return res?.data ?? res ?? [];
  }

  async obtenerVacantesPorCategoria(categoria: string): Promise<Response> {
    return await this.httpClient.get<Response>(`${this.baseApiUrl}/publico/vacantes/categoria/${categoria}`).toPromise();
  }

  async obtenerVacantesPorIdEmpresa(idEmpresa: number, rol: string): Promise<Response> {
    return await this.httpClient.get<Response>(`${this.baseApiUrl}/privado/vacante/empresa/${idEmpresa}/${rol}`).toPromise();
  }

  async obtenerDetallesVacante(idVacante: number, urlVacante: string): Promise<any> {
    const slug = urlVacante || '-';
    return await this.httpClient.get<any>(`${this.baseApiUrl}/publico/vacante/${idVacante}/${slug}`).toPromise();
  }

  async obtenerModalidadesEmpleo(): Promise<Response> {
    return await this.httpClient.get<Response>(`${this.baseApiUrl}/privado/vacante/modalidad-empleo`).toPromise();
  }

  async obtenerCursosRelacionados(idCategoria: number): Promise<Response> {
    if (idCategoria == null || idCategoria <= 0) {
        throw new Error('El ID de la categoría es inválido.');
    }

    const url = `${this.baseApiUrl}/publico/vacante/cursos-relacionados/${idCategoria}`;
    console.log('URL de la solicitud:', url);
    return await this.httpClient.get<Response>(url).toPromise();
}


  async obtenerVacantesFiltroPorEmpresa(idEmpresa: number): Promise<Response> {
    return await this.httpClient.get<Response>(`${this.baseApiUrl}/privado/vacante-empresa/${idEmpresa}`).toPromise();
  }

  crearVacanteEmpresa(idEmpresa: number, body: VacanteDTO): Observable<Response> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.post<Response>(`${this.baseApiUrl}/privado/vacante/${idEmpresa}`, body, { headers });
  }

  actulizarVacanteEmpresa(body: VacanteDTO): Observable<Response> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.put<Response>(`${this.baseApiUrl}/privado/vacante`, body, { headers });
  }

  actulizarStatusVacanteEmpresa(idVacante: number, status: boolean): Observable<Response> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.put<Response>(`${this.baseApiUrl}/privado/vacante/${idVacante}/status/${status}`, { headers });
  }

}
